import { ChangeDetectorRef, Component, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../../../../shared/shared-imports';
import { LookupKey } from '../../../../../../shared/constants/lookup-keys';
import { Subject, combineLatest, startWith, takeUntil } from 'rxjs';
import { FieldValue, DocumentServiceType } from '../../../../../../shared/constants/services.enum';
import { UserModel } from '../../../../../../shared/models/loginuser.model';
import { OptionItem } from '../../../../../../shared/models/option-translation.model';
import { OptionTranslateService } from '../../../../../../shared/services/option-translate.service';
import { SharedService } from '../../../../../../shared/services/shared.service';
import { Utils } from '../../../../../../Utils/utils';
import { customValidator } from '../../../../../../Utils/validator';
import { FolderResponse } from '../../../../../../shared/models/documents-response.model';
import { FileComponent } from '../../../../../../shared/components/controls/file/file.component';
import { SkeletonLoaderComponent } from "../../../../../../shared/components/skeleton-loader/skeleton-loader.component";
import { ApiEndPoints, ApiRoot } from '../../../../../../shared/constants/api-endpoints';
import { ApiService } from '../../../../../../core/services/api.service';
import { DocumentName } from '../../../../../../shared/constants/documents-name.enum';
import { EncryptionResponse } from '../../../../../../shared/models/encryption-response.model';
import { AttachmentService } from '../../../../../../core/services/attachment.service';

@Component({
  selector: 'app-add-signatory',
  imports: [ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS, SkeletonLoaderComponent],
  templateUrl: './add-signatory.component.html',
  styleUrl: './add-signatory.component.scss'
})
export class AddSignatoryComponent {
  @ViewChildren('fileCmp') fileComponents!: QueryList<FileComponent>;
  private destroy$ = new Subject<void>();
  @Input() isOpen = false;
  @Input() title = '';
  @Input() totalShare = 0;
  @Input() data: any = "";
  @Input() allMembersData: any[] = [];
  @Input() showAction: boolean = false;
  @Input() srFolder!: FolderResponse;
  @Input() countryList: any[] = [];


  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() onBtnClick = new EventEmitter<any>();
  @Output() folderMissing = new EventEmitter<void>();
  fieldValue = FieldValue;
  LookupKey: any = LookupKey;
  documentServiceType = DocumentServiceType;

  form!: FormGroup;
  nationalities: OptionItem[] = [];
  roles: any = [];
  partnerTypes: OptionItem[] = [];
  standardOption: OptionItem[] = [];
  tyeOfAddUser: OptionItem[] = [];

  detailFound: boolean = false;
  disableCompanyOption = false;
  loginUserDetail!: UserModel;
  loadingEmirateInfo: boolean = false;
  detailNotFound: boolean = false;
  showSameNameMsg: boolean = false;
  lang: string = "en";
  private lastNotes: string | null = null;

  constructor(
    private fb: FormBuilder,
    private optionTranslateService: OptionTranslateService,
    private sharedService: SharedService,
    private cdr: ChangeDetectorRef,
    private api: ApiService,
    private attachmentService: AttachmentService
  ) {
    this.lang = sharedService.getCurrentLang() ?? 'en';
    this.form = this.fb.group({
      roles: [[],],
      typeOfAddUser: ['New profile', [Validators.required]],
      partnerType: ["Individual"],
      isResident: [null],
      /** UAE-resident fields */
      emiratesId: [null, [customValidator('emiratesId'), customValidator('safeText'), Validators.maxLength(20)]],
      emiratesDob: this.fb.group({
        day: [{ value: null, disabled: true }, [Validators.required]],
        month: [{ value: null, disabled: true }, [Validators.required]],
        year: [{ value: null, disabled: true }, [Validators.required]]
      }),
      /** Non-resident fields */
      passportNumber: [null, [customValidator('safeText'), Validators.maxLength(30)]],
      passportExpiry: this.fb.group({
        day: [null],
        month: [null],
        year: [null],
      }),
      /** Individual fields */
      nameEn: [null, [customValidator('english'), Validators.maxLength(200)]],
      nameAr: new FormControl(null, {
        validators: [customValidator('arabic'), Validators.maxLength(200)],
        updateOn: 'blur'
      }),
      nationality: [null],
      /** Company fields */
      companyNameEn: [null, [customValidator('english'), Validators.maxLength(200)]],
      companyNameAr: [null, [customValidator('arabic'), Validators.maxLength(200)]],
      /** Owner/Partner only */
      share: [null],
      mobile: [null, [Validators.required, customValidator('mobile')]],
      countryCode: [null, [Validators.required]],
      email: [null, [customValidator('email'), Validators.maxLength(200)]],
      documentTradeLicense: [''],
      documentPassport: [''],
      documentEmiratesId: [],
    });

    this.loginUserDetail = this.sharedService.getUserProfile(); //this.authService.getUserClaims();
    //get options value
    const lang = (localStorage.getItem('selectedLang') as 'en' | 'ar') || 'en';
    this.nationalities = this.optionTranslateService.getOptions(
      'nationalities',
      lang
    );
    this.roles = this.optionTranslateService.getOptions('roles', lang);
    this.partnerTypes = this.optionTranslateService.getOptions(
      'partnerTypes',
      lang
    );
    this.standardOption = this.optionTranslateService.getOptions(
      'standardOption',
      lang
    );
    this.tyeOfAddUser = this.optionTranslateService.getOptions(
      'tyeOfAddUser',
      lang
    );
  }

  // This method is used to handle the on init lifecycle hook
  ngOnInit(): void {
    if (this.data) {
      this.updateValue();
    }

    combineLatest([
      this.form
        .get('typeOfAddUser')!
        .valueChanges.pipe(startWith(this.form.get('typeOfAddUser')!.value)),
      this.form
        .get('partnerType')!
        .valueChanges.pipe(startWith(this.form.get('partnerType')!.value)),
      this.form
        .get('isResident')!
        .valueChanges.pipe(startWith(this.form.get('isResident')!.value)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateConditionalBehaviour());

    /* enforce “Individual” whenever the relevant switches change */
    this.form
      .get('roles')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.enforceIndividualWhenRestricted());
    this.form
      .get('typeOfAddUser')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.enforceIndividualWhenRestricted());


    // initialize last notes and watch for changes that impact notes
    this.lastNotes = (this.getNotesValue() || '').toLowerCase();

    const nameEn$ = this.form.get('nameEn')!.valueChanges.pipe(startWith(this.form.get('nameEn')!.value));
    const companyNameEn$ = this.form.get('companyNameEn')!.valueChanges.pipe(startWith(this.form.get('companyNameEn')!.value));
    const emiratesId$ = this.form.get('emiratesId')!.valueChanges.pipe(startWith(this.form.get('emiratesId')!.value));
    const passportNumber$ = this.form.get('passportNumber')!.valueChanges.pipe(startWith(this.form.get('passportNumber')!.value));

    combineLatest([nameEn$, companyNameEn$, emiratesId$, passportNumber$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const currentNotes = (this.getNotesValue() || '').toLowerCase();
        if (this.lastNotes && currentNotes && this.lastNotes !== currentNotes) {
          this.removeIdentityAndTradeDocsByNotes(this.lastNotes);
        }
        this.lastNotes = currentNotes;
      });
  }

  // This method is used to update value
  updateValue() {
    this.form.patchValue({
      roles: this.data.roles,
      typeOfAddUser: this.data.typeOfAddUser,
      partnerType: this.data.partnerType,
      isResident: this.data.isResident,
      emiratesId: this.data.emiratesId,
      emiratesDob: {
        day: this.data.emiratesDob?.day,
        month: this.data.emiratesDob?.month,
        year: this.data.emiratesDob?.year
      },
      passportNumber: this.data.passportNumber,
      passportExpiry: {
        day: this.data.passportExpiry?.day,
        month: this.data.passportExpiry?.month,
        year: this.data.passportExpiry?.year
      },
      nameEn: this.data.nameEn,
      nameAr: this.data.nameAr,
      nationality: this.data.nationality,
      companyNameEn: this.data.companyNameEn,
      companyNameAr: this.data.companyNameAr,
      share: Number(this.data.share),
      mobile: this.data.mobile,
      countryCode: this.data.countryCode,
      email: this.data.email
    });
    if (!this.showAction) {
      this.form.disable();
    } else if (this.data?.ownerId && !this.data?.isNewSignatory) {
      // Existing signatory: names are read-only, other fields editable.
      this.form.get('nameEn')?.disable({ emitEvent: false });
      this.form.get('nameAr')?.disable({ emitEvent: false });
    }
    if (this.data.emiratesId) {
      this.detailFound = true;
    }
  }

  // This method is used to enforce individual when restricted
  private enforceIndividualWhenRestricted(): void {
    const roles: string[] = this.form.get('roles')!.value || [];

    const hasManagerOrSignatory = roles.includes('Shares Owner');
    const isAddMyself = this.form.get('typeOfAddUser')!.value === 'Myself';
    if (hasManagerOrSignatory || isAddMyself || (this.form.get('typeOfAddUser')!.value === 'New profile' && this.form.get('roles')!.value.includes("1"))) {
      // Force partner-type to “Individual” (value '1')
      this.form.get('partnerType')!.setValue('Individual', { emitEvent: false });
    }
  }

  // This method is used to update conditional behaviour
  private updateConditionalBehaviour(): void {
    const roles: string[] = this.form.get('roles')!.value || [];
    const isAddMyself = this.form.get('typeOfAddUser')!.value === 'Myself';
    if (isAddMyself && this.loginUserDetail) {
      if (this.data && this.loginUserDetail['First Name'] == this.data?.nameEn) {
        if (this.loginUserDetail?.['Emirates Id']) {
          this.form.get("isResident")?.setValue(this.lang == 'ar' ? FieldValue.YES_AR : FieldValue.YES);
        } else {
          this.form.get("isResident")?.setValue(this.lang == 'ar' ? FieldValue.NO_AR : FieldValue.NO);
        }
      } else {
        this.form.get("nameEn")?.setValue(this.loginUserDetail['Name English'] ? this.loginUserDetail['Name English'] : `${this.loginUserDetail['First Name']}`);
        this.form.get("nameAr")?.setValue(this.loginUserDetail['Name Arabic'] ? this.loginUserDetail['Name Arabic'] : this.loginUserDetail['Last Name']);
        this.form.get("email")?.setValue(this.loginUserDetail['EMail Addr']);
        this.form.get("mobile")?.setValue(Utils.parseMobile(this.loginUserDetail['Phone #'], 'number'));
        this.form.get("countryCode")?.setValue(Utils.parseMobile(this.loginUserDetail['Phone #'], 'cc'));
        this.form.get("nationality")?.setValue(this.loginUserDetail['DC Nationality']);
        if (this.loginUserDetail?.['Emirates Id']) {
          this.form.get("isResident")?.setValue(this.lang == 'ar' ? FieldValue.YES_AR : FieldValue.YES);
          this.form.get("emiratesId")?.setValue(this.loginUserDetail?.['Emirates Id']);
        } else {
          this.form.get("isResident")?.setValue(this.lang == 'ar' ? FieldValue.NO_AR : FieldValue.NO);
        }
      }
    }

    /* Disable the “Company” option when rules say so */
    const hasManagerOrSignatory = roles.includes('Manager') || roles.includes('Authorised Signatory');

    this.disableCompanyOption = hasManagerOrSignatory || isAddMyself;

    /* ---------- Residency & partner-type specific validators ----------- */
    const resident = (this.form.get('isResident')!.value === FieldValue.YES || this.form.get('isResident')!.value === FieldValue.YES_AR);
    const nonResident = (this.form.get('isResident')!.value === FieldValue.NO || this.form.get('isResident')!.value === FieldValue.NO_AR);
    const individual = this.form.get('partnerType')!.value === 'Individual';
    const company = this.form.get('partnerType')!.value === 'Company';
    const onlyOwner = roles.length > 0 && roles.includes('Shares Owner');

    this.toggleRequired(resident && individual, [
      'emiratesId',
      'emiratesDob.day',
      'emiratesDob.month',
      'emiratesDob.year',
      // 'documentEmiratesId'
    ]);
    this.toggleRequired((nonResident && individual), [
      'passportNumber',
      'passportExpiry.day',
      'passportExpiry.month',
      'passportExpiry.year',
      // 'documentPassport'
    ]);

    this.toggleRequired(individual, ['isResident', 'nameEn', 'nameAr']);

    this.toggleRequired(company, [
      'companyNameEn',
      'companyNameAr',
      'documentTradeLicense'
    ]);

    // this.toggleRequired(onlyOwner, ['share']);
    this.toggleRequired((isAddMyself || individual || company), ['countryCode', 'mobile', 'email']);
    this.toggleRequired(!isAddMyself, ['nationality'])

  }

  // This method is used to toggle required using condition and controls
  private toggleRequired(condition: boolean, controls: string[]): void {
    Utils.addRemoveValidation(this.form, condition, controls);
  }


  // This method is used to get detail by emirate id
  async getDetailByEmirateId() {
    if (this.form.get('emiratesId')?.invalid) {
      return;
    }
    this.detailNotFound = false;
    this.detailFound = false;
    this.loadingEmirateInfo = false;

    let emirateId: any = this.form.get('emiratesId');
    if (emirateId.value) {
      this.loadingEmirateInfo = true;
      this.getPersonDetail(emirateId.value);
    }
  }

  // This method is used to resolve nationality value from country list
  private getMatchedCountryValue(userNationality: string): string | null {
    const normalizedNationality = (userNationality || '').trim().toLowerCase();
    if (!normalizedNationality || !Array.isArray(this.countryList)) {
      return null;
    }

    const matchedCountry = this.countryList?.find((country: any) =>
      (country?.Name || '').trim().toLowerCase() === normalizedNationality
    );

    return matchedCountry?.Value || null;
  }

  // This method is used to get person detail using emirate id
  getPersonDetail(emirateId: string) {
    const requestPayload = {
      identityNumber: emirateId
    };
    this.api.post(
      ApiRoot.ICA_PERSON_INQUIRY,
      ApiEndPoints.GET_PERSON_INQUIRY,
      requestPayload
    ).subscribe({
      // This method is used to handle the next callback using response
      next: (response: any) => {
        if (response && response.errorCode == null) {
          this.detailFound = true;
          this.loadingEmirateInfo = false;
          const profile = response?.personProfileType;
          if (profile) {
            const birthDate = profile?.birthDate;
            const date = birthDate ? new Date(birthDate) : null;
            const fullDate = date && !isNaN(date.getTime())
              ? {
                day: String(date.getDate()).padStart(2, '0'),
                month: String(date.getMonth() + 1).padStart(2, '0'),
                year: date.getFullYear()
              }
              : { day: '', month: '', year: '' };
            this.detailFound = true;

            const emailAddress = profile?.addresses?.address?.localAddress?.emailAddress ?? '';
            const mobileNo = profile?.addresses?.address?.localAddress?.mobileNo ?? '';
            const userNationality = profile?.nationality?.descriptionEnglish ?? '';
            const nationality = this.getMatchedCountryValue(userNationality);
            const fullNameArabic = profile?.personName?.fullNameArabic ?? '';
            const fullNameEnglish = profile?.personName?.fullNameEnglish ?? '';
            this.form.controls['emiratesDob']?.setValue(fullDate);
            this.form.get('emiratesDob')?.updateValueAndValidity();

            this.form.get('nameEn')?.setValue(fullNameEnglish);
            this.form.get('nameAr')?.setValue(fullNameArabic);
            this.form.get('nationality')?.setValue(nationality);
            this.form.get('mobile')?.setValue(mobileNo);
            this.form.get('email')?.setValue(emailAddress);
          } else {
            this.detailFound = false;
            this.detailNotFound = true;

            this.form.get('nameEn')?.setValue('');
            this.form.get('nameAr')?.setValue('');
            this.form.get('nationality')?.setValue(null);
            this.form.get('mobile')?.setValue('');
            this.form.get('email')?.setValue('');
          }
          this.cdr.detectChanges();
        } else {
          this.detailFound = false;
          this.detailNotFound = true;
          this.loadingEmirateInfo = false;
          this.cdr.detectChanges();
        }
      },
      // This method is used to handle the error callback
      error: () => {
        this.detailFound = false;
        this.detailNotFound = true;
        this.loadingEmirateInfo = false;
        this.cdr.detectChanges();
      }
    })
  }

  // This method is used to handle the on destroy lifecycle hook
  ngOnDestroy(): void {
  }

  // This method is used to handle file change event using event and field
  onFileChange(event: any, field: string) {
    const file = event.target.files?.[0];
    this.form.patchValue({ [field]: file });
  }

  // Submit owner/manager detail
  showShareMsg: boolean = false;
  invalidFieldMsg: any = "";
  showPassportOrSignatureMsg = false;
  // This method is used to submit owner
  submitOwner() {
    this.showPassportOrSignatureMsg = false;
    const invalidControls = Utils.getInvalidControls(this.form);
    console.warn('Invalid controls:', invalidControls);
    const controlLabels: Record<string, string> = {
      emiratesId: 'Emirates ID',
      passportNo: 'Passport Number',
      licenceNumber: 'License Number'
    };

    let formData = this.form.getRawValue();
    if (this.form.get('isResident')?.value == FieldValue.YES || this.form.get('isResident')?.value == FieldValue.YES_AR) {
      if (formData.emiratesId == "" || (formData.emiratesDob?.day == null || formData.emiratesDob?.day == "")) {
        this.detailNotFound = true;
        return;
      }
    }

    // Show invalid filed error
    if (invalidControls.length > 0) {
      let messages = Utils.getInvalidFieldMessages(invalidControls, controlLabels);
      this.invalidFieldMsg = messages.join('\n');
    }
    if (this.form.invalid || this.checkRequiredDocuments()) {
      this.form.markAllAsTouched();
      const invalidControls = Utils.getInvalidControls(this.form);
      return;
    }

    const hasPassportCopy = this.sharedService.hasDocumentUploaded(this.srFolder, this.getNotesValue(), 'passport_or_emirates_id_copy');
    const hasSignatureCard = this.sharedService.hasDocumentUploaded(this.srFolder, this.getNotesValue(), 'signature_card');
    if ((!hasPassportCopy && !hasSignatureCard)) {
      this.showPassportOrSignatureMsg = true;
      return;
    }

    //let formData = this.form.getRawValue();
    // Check if updating an existing owner
    if (this.data?.ownerId) {
      formData['ownerId'] = this.data.ownerId;
    } else {
      let name = formData.partnerType == "Individual" ? formData.nameEn : formData.companyNameEn;
      let isMemberExistWithName = this.allMembersData.find((member: any) => member.nameEn == name || member.companyNameEn == name);
      if (isMemberExistWithName) {
        this.showSameNameMsg = true;
        return
      }
    }
    formData.emiratesId = formData.emiratesId ? formData.emiratesId.replace(/-/g, '') : "";
    this.onBtnClick.emit(formData);
    this.close();
  }

  // This method is used to close
  close() {
    this.isOpen = false;
    this.isOpenChange.emit(false);
  }

  // This method is used to handle nationality change event using selected
  onNationalityChange(selected: any) {
    const value = this.form.get('nationality')?.value;
    const mobileControl = this.form.get('mobile');
    const countryCodeControl = this.form.get('countryCode');
    if (!mobileControl || !countryCodeControl) {
      return;
    }
    if (value == FieldValue.UAE || value == FieldValue.UAE_AR) {
      mobileControl.setValidators([Validators.required, customValidator('mobile', 'UAE')]);
      countryCodeControl.setValue("971");
      countryCodeControl.disable();
    } else {
      mobileControl.setValidators([Validators.required, customValidator('mobile')]);
      countryCodeControl.enable();
    }
    mobileControl.updateValueAndValidity({ emitEvent: false });
    countryCodeControl.updateValueAndValidity({ emitEvent: false });
  }
  // This method is used to handel file event using document and document field
  handelFileEvent(document: any, documentField: any) {
    const lowerField = (documentField || '').toString().toLowerCase();
    if (lowerField === 'passport_or_emirates_id_copy' || lowerField === 'signature_card') {
      this.showPassportOrSignatureMsg = !(this.sharedService.hasDocumentUploaded(this.srFolder, this.getNotesValue(), 'passport_or_emirates_id_copy') || this.sharedService.hasDocumentUploaded(this.srFolder, this.getNotesValue(), 'signature_card'));
    }
  }
  // This method is used to get person documents
  get partnerDocuments() {
    const partnerType = this.form.get('partnerType')?.value;
    return this.sharedService.getOwnerPartnerDocuments(this.srFolder, partnerType);

  }

  canUploadPartnerDocuments(): boolean {
    if (!this.showAction) {
      return false;
    }
    const partnerType = this.form.get('partnerType')?.value;
    const companyNameEnControl = this.form.get('companyNameEn');
    const nameEnControl = this.form.get('nameEn');

    if (partnerType === FieldValue.COMPANY) {
      return !!companyNameEnControl?.value?.trim() && (companyNameEnControl?.disabled || companyNameEnControl?.valid === true);
    }

    const isEnglishNameValid = !!nameEnControl?.value?.trim() && (nameEnControl?.disabled || nameEnControl?.valid === true);
    const hasIdentityDocumentNumber = !!this.form.get('emiratesId')?.value?.trim() || !!this.form.get('passportNumber')?.value?.trim();

    return isEnglishNameValid && hasIdentityDocumentNumber;
  }

  // This method is used to check required documents
  checkRequiredDocuments(): boolean {
    let hasMissing = false;
    this.partnerDocuments?.forEach((doc: any, index: any) => {
      const cmp = this.fileComponents.toArray()[index];
      if (cmp.checkMissing(doc.mandatory)) {
        hasMissing = true;
      }
    });
    return hasMissing;
  }

  // This method is used to get notes value
  getNotesValue() {
    let notes = "";
    if (this.form.get('partnerType')?.value == FieldValue.COMPANY && this.form.get('companyNameEn')?.value) {
      notes = this.form.get('companyNameEn')?.value;
    } else {
      if (this.form.get('emiratesId')?.value && (this.form.get('isResident')?.value == FieldValue.YES || this.form.get('isResident')?.value == FieldValue.YES_AR)) {
        notes = `${this.form.get('emiratesId')?.value}_${this.form.get('nameEn')?.value}`;
      } else {
        notes = `${this.form.get('passportNumber')?.value}_${this.form.get('nameEn')?.value}`;
      }
    }
    return notes;
  }

  private removeIdentityAndTradeDocsByNotes(prevNotes: string): void {
    try {
      const folder = this.srFolder;
      if (!folder || !Array.isArray(folder.documents) || !prevNotes) return;

      const toDelete = folder.documents.filter((doc: any) => {
        const noteMatch = (doc?.notes || '').toLowerCase() === prevNotes.toLowerCase();
        const dt = (doc?.documentType || '').toString().toLowerCase();
        const isPassportOrEmirates = dt.includes('passport') || dt.includes('emirates') || dt.includes('signature_card');
        const isTradeLicense = dt.includes('license') || dt.includes('licence');
        return noteMatch && (isPassportOrEmirates || isTradeLicense);
      });

      // Optimistically remove from UI and trigger child update
      if (toDelete.length > 0) {
        const remaining = (folder.documents || []).filter((d: any) => !toDelete.some((x: any) => x.documentId === d.documentId));
        this.srFolder = {
          ...folder,
          documents: [...remaining],
        } as FolderResponse;
        this.cdr.markForCheck();
      }
      this.sharedService
        .getEncryption(this.documentServiceType.MEMBERSHIP)
        .subscribe({
          // This method is used to handle the next callback using encryption response
          next: (encryptionResponse: EncryptionResponse) => {
            if (!encryptionResponse) {
              return;
            }
            // Step 2: Decode response and stringify
            const response: any = Utils.decodeAndStringify(encryptionResponse);
            toDelete.forEach((doc: any) => {
              this.attachmentService
                .deleteRequest(ApiRoot.JUPITER_REST, ApiEndPoints.DELETE_DOCUMENT, {
                  documentId: doc.documentId,
                  folderId: folder.folderId,
                  folderParentId: folder.folderParentId,
                  folderParentType: folder.folderParentType,
                  validation: response.validation,
                })
                .subscribe({
                  next: () => {
                  }
                });
            });
          }
        })
    } catch (e) {
      // swallow to avoid blocking UX
    }
  }

  downloadTemplateFile() {
    const link = document.createElement('a');
    link.href = DocumentName.SIGNATURE_CARD;
    link.target = '_blank';
    // document.body.appendChild(link);
    link.click();
    // document.body.removeChild(link);
  }
}
